import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";

import { StatusIndicator } from "components/partials/status-indicator/status-indicator";
import TouchpointsTableActionsMenu from "containers/admin/clients/client/approval-queue/batch-details/components/touchpoints-table-actions-menu/touchpoints-table-actions-menu";

import { TouchpointStatusDictionaryItem } from "hooks/api/dictionary";

import { getStatuses } from "components/partials/selected-items/selected-items.helper";
import {
  buildTouchpointPreviewHref,
  getTouchpointPaths,
  buildPath,
  Route as AppRoute,
} from "utilities/app-routes";
import { getDisplayTouchpointIcon, getDisplayTouchpointType } from "utilities";
import { formatDateTime } from "utilities/date";

import { TouchpointItem } from "containers/admin/clients/client/approval-queue/approval-queue.types";
import { TouchpointType, TouchpointTypeLabel } from "types/touchpoint";

import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";
import { Chip } from "ui/chip";

import { truncateBatchName } from "../../batch-details.utils";

interface TableActionsProps {
  clientId: string;
  batchId: string;
  batchName: string;
  touchpointVersionStatuses: TouchpointStatusDictionaryItem[];
  hasTemplates: boolean;
  onEdit: (id: string) => void;
  onOpenInEditor: (id: string) => void;
  onCreateDraft: (id: string) => void;
  onClone: (id: string) => void;
  onRemoveFromQueue: (id: string) => Promise<void>;
}

const headerStyles = "text-gray-600 font-bold leading-5 px-4 py-3";

export const createBatchTableColumns = ({
  clientId,
  batchId,
  batchName,
  touchpointVersionStatuses,
  hasTemplates,
  onEdit,
  onOpenInEditor,
  onCreateDraft,
  onClone,
  onRemoveFromQueue,
}: TableActionsProps): ColumnDef<TouchpointItem>[] => {
  const approvalQueuePath = buildPath(AppRoute.clients, clientId, AppRoute.approvalQueue);
  const batchPath = buildPath(approvalQueuePath, AppRoute.batch, batchId);
  const batchCrumbs = [
    { label: "Approval queue", href: approvalQueuePath },
    { label: truncateBatchName(batchName), href: batchPath },
  ];
  return [
    {
      id: "type",
      header: "Type",
      accessorKey: "type",
      enableSorting: true,
      cell: ({ row }) => {
        const touchpointType = row.original.type as TouchpointType;
        const isTemplate = row.original.campaignName === PROGRAM_TEMPLATES_CAMPAIGN_NAME;
        const icon = getDisplayTouchpointIcon(touchpointType);
        const title =
          touchpointType === TouchpointType.OTHER
            ? TouchpointTypeLabel.OTHER
            : getDisplayTouchpointType(touchpointType);

        return (
          <div className="flex items-center gap-2">
            <div className="size-4 mt-0.5">{icon}</div>
            <span className="text-gray-800 font-medium">{title}</span>
            {isTemplate && <Chip variant="filled" size="small" label="Template" />}
          </div>
        );
      },
      size: hasTemplates ? 130 : 60,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "touchpointName",
      header: "Creative Name",
      accessorKey: "touchpointName",
      enableSorting: true,
      cell: ({ row }) => {
        const link = buildTouchpointPreviewHref(
          getTouchpointPaths({
            clientId,
            campaignId: row.original.campaignId,
            touchpointId: row.original.touchpointId,
          }).touchpointPreviewPath,
          row.original.id,
        );
        const status = getStatuses(row.original.status, touchpointVersionStatuses);

        return (
          <div className="flex flex-col justify-center">
            <Link
              to={link}
              state={{
                fromSection: AppRoute.approvalQueue,
                breadcrumbs: batchCrumbs,
              }}
              className="text-aqua-600 hover:text-aqua-700 font-semibold line-clamp-1"
              aria-disabled={false}
              tabIndex={undefined}>
              {row.original.touchpointName}
            </Link>
            <span className="text-gray-500 text-sm">
              v {row.original.version} ({status.versionStatusName})
            </span>
          </div>
        );
      },
      size: 380,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "campaignName",
      header: "Campaign Name",
      accessorKey: "campaignName",
      enableSorting: true,
      cell: ({ row }) => {
        return (
          <span className="text-gray-600 font-medium line-clamp-1">
            {row.original.campaignName}
          </span>
        );
      },
      size: 200,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "status",
      header: "Client review status",
      accessorKey: "status",
      enableSorting: true,
      cell: ({ row }) => {
        const status = getStatuses(row.original.status, touchpointVersionStatuses);

        return <StatusIndicator versionStatuses={status} />;
      },
      size: 140,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "addedToBatchDate",
      header: "Added to batch",
      accessorKey: "addedToBatchDate",
      enableSorting: true,
      cell: ({ row }) => (
        <div className="font-medium text-gray-600">
          {formatDateTime(row.original.addedToBatchDate)}
        </div>
      ),
      size: 140,
      meta: {
        headerClassName: headerStyles,
      },
    },

    {
      id: "comments",
      header: "Comments",
      accessorKey: "comments",
      enableSorting: false,
      cell: ({ row }) => {
        return <div className="text-gray-600 text-center">{row.original.openCommentCount}</div>;
      },
      size: 150,
      meta: {
        isHeaderCentered: true,
        headerClassName: headerStyles,
      },
    },
    {
      id: "actions",
      header: "Action",
      enableSorting: false,
      cell: ({ row }) => (
        <TouchpointsTableActionsMenu
          item={{ itemName: row.original.touchpointName, itemId: row.original.id }}
          touchpointType={row.original.type}
          {...{ onEdit, onOpenInEditor, onCreateDraft, onClone, onRemoveFromQueue }}
        />
      ),
      size: 66,
      meta: {
        className: "pr-8",
        isHeaderCentered: true,
        headerClassName: headerStyles,
        isPinnedRight: true,
      },
    },
  ];
};
